import { gql, type DocumentNode } from '@apollo/client';

export const profileSections = [
  { key: 'Identity', label: 'Identity', read: 'program:participant~read', write: 'program:participant~write' },
  { key: 'Legal', label: 'Legal', read: 'program:participant:legal~read', write: 'program:participant:legal~write' },
  { key: 'Medical', label: 'Medical', read: 'program:participant:medical~read', write: 'program:participant:medical~write' },
  { key: 'Plan', label: 'Plan and location', read: 'program:participant:disposition~read', write: 'program:participant:disposition~write' },
  { key: 'Care', label: 'Coordinated care', read: 'program:participant:coordinatedcare~read', write: 'program:participant:coordinatedcare~write' },
  { key: 'Enrollment', label: 'Insurance and enrollment', read: 'program:participant:enrollment~read', write: 'program:participant:enrollment~write' },
  { key: 'Notes', label: 'Notes', read: 'program:participant:notes~read', write: 'program:participant:notes~full' },
  { key: 'Events', label: 'Major life events', read: 'program:participant~read', write: 'admin:access~full' },
  { key: 'Documents', label: 'Documents', read: 'program:participant:documentscans~full', write: 'program:participant:documentscans~full' },
  { key: 'Identifiers', label: 'Identifiers', read: 'admin:access~full', write: 'admin:access~full' },
  { key: 'Medcall', label: 'MedCall', read: 'tools:medcall:access:~user', write: 'tools:medcall:access:~full' },
] as const;
export type ProfileSection = typeof profileSections[number];
export type ProfileView = ProfileSection['key'] | 'Incidents' | 'Notifications' | 'MedcallTimeline';

export const PROFILE_FIELDS = gql`
  fragment ProfileFields on ParticipantProfileSection {
    odrnumber blocked primary
    links { participant active }
    entities {
      name label collection archivable stored hasMore next
      fields { name label type options required editable objectFields { name label format } }
      records {
        key revision timestamp author participant needsSync removed canonical
        values { name value }
        unifiedValues { name value }
      }
    }
  }
`;

export const profileDictionaries: Record<string, DocumentNode> = {
  'location.location': gql`
    query ParticipantProfilePlanDictionary($odrnumber: Int!, $entity: String!, $field: String!, $search: String!, $after: Int) {
      dictionary: participantProfilePlanDictionary(odrnumber: $odrnumber, entity: $entity, field: $field, search: $search, after: $after) {
        items { value label } hasMore next
      }
    }
  `,
  'icd10.icd10code': gql`
    query ParticipantProfileMedicalDictionary($odrnumber: Int!, $entity: String!, $field: String!, $search: String!, $after: Int) {
      dictionary: participantProfileMedicalDictionary(odrnumber: $odrnumber, entity: $entity, field: $field, search: $search, after: $after) {
        items { value label } hasMore next
      }
    }
  `,
  'charges.charge': gql`
    query ParticipantProfileLegalDictionary($odrnumber: Int!, $entity: String!, $field: String!, $search: String!, $after: Int) {
      dictionary: participantProfileLegalDictionary(odrnumber: $odrnumber, entity: $entity, field: $field, search: $search, after: $after) {
        items { value label } hasMore next
      }
    }
  `,
};

export const SAVE_PROFILE_MEDICATION = gql`
  mutation SaveParticipantProfileMedication($odrnumber: Int!, $key: String!, $revision: String, $fields: [ParticipantProfileChange!]!, $generateTimeline: Boolean!) {
    saveParticipantProfileMedcall: saveParticipantProfileMedication(odrnumber: $odrnumber, key: $key, revision: $revision, fields: $fields, generateTimeline: $generateTimeline) { success id key written warnings }
  }
`;

export const SAVE_PROFILE_CARE = gql`
  mutation SaveParticipantProfileCoordinatedCare($odrnumber: Int!, $key: String!, $revision: String, $fields: [ParticipantProfileChange!]!, $conference: Boolean!, $deriveCode: Boolean!) {
    saveParticipantProfileCare: saveParticipantProfileCoordinatedCare(odrnumber: $odrnumber, key: $key, revision: $revision, fields: $fields, conference: $conference, deriveCode: $deriveCode) { success id key written warnings }
  }
`;

export const PROFILE_DOCUMENT_DEFAULTS = gql`
  query ParticipantProfileDocumentDefaults($odrnumber: Int!) {
    participantProfileDocumentDefaults(odrnumber: $odrnumber) { values { name value } }
  }
`;

export const PROFILE_INSURANCE = gql`
  query ParticipantProfileInsurance($odrnumber: Int!) {
    participantProfileInsurance(odrnumber: $odrnumber) { revision values { name value } choices { name options { value label } } }
  }
`;
export const SAVE_PROFILE_INSURANCE = gql`
  mutation SaveParticipantProfileInsurance($odrnumber: Int!, $revision: String!, $fields: [ParticipantProfileChange!]!) {
    saveParticipantProfileInsurance(odrnumber: $odrnumber, revision: $revision, fields: $fields) { success id key written warnings }
  }
`;

export const PROFILE_MEDCALL_OPTIONS = gql`
  query ParticipantProfileMedcallOptions($after: Int) {
    participantProfileMedcallOptions(after: $after) { operator staff { id fullname username } hasMore next }
  }
`;
export const PROFILE_MEDCALL_FACILITIES = gql`
  query ParticipantProfileMedcallFacilities($after: Int) {
    participantProfileMedcallFacilities(after: $after) { items { value label } hasMore next }
  }
`;
export const CREATE_PROFILE_MEDCALL = gql`
  mutation CreateParticipantProfileMedcall($odrnumber: Int!, $participant: Int!, $revision: String!, $program: String!, $fields: [ParticipantProfileChange!]!) {
    createParticipantProfileMedcall(odrnumber: $odrnumber, participant: $participant, revision: $revision, program: $program, fields: $fields) { success written warnings }
  }
`;
export const ASSIGN_PROFILE_MEDCALL_STAFF = gql`
  mutation AssignParticipantProfileMedcallStaff($odrnumber: Int!, $revision: String!, $staff: Int!) {
    change: assignParticipantProfileMedcallStaff(odrnumber: $odrnumber, revision: $revision, staff: $staff) { success written warnings }
  }
`;
export const SET_PROFILE_MEDCALL_ACTIVE = gql`
  mutation SetParticipantProfileMedcallActive($odrnumber: Int!, $revision: String!, $active: Boolean!, $reason: String, $backDate: String, $generateTimeline: Boolean!) {
    change: setParticipantProfileMedcallActive(odrnumber: $odrnumber, revision: $revision, active: $active, reason: $reason, backDate: $backDate, generateTimeline: $generateTimeline) { success written warnings }
  }
`;
export const SET_PROFILE_MEDCALL_MY_CLIENT = gql`
  mutation SetParticipantProfileMedcallMyClient($odrnumber: Int!, $revision: String!, $included: Boolean!) {
    change: setParticipantProfileMedcallMyClient(odrnumber: $odrnumber, revision: $revision, included: $included) { success written warnings }
  }
`;

export const profileQueries = Object.fromEntries(profileSections.map(section => [section.key, gql`
  query ParticipantProfile${section.key}($odrnumber: Int!, $entity: String, $key: String, $history: Boolean, $offset: Int, $filters: [ParticipantProfileChange!]) {
    participantProfile${section.key}(odrnumber: $odrnumber, entity: $entity, key: $key, history: $history, offset: $offset, filters: $filters) { ...ProfileFields }
  }
  ${PROFILE_FIELDS}
`])) as Record<ProfileSection['key'], DocumentNode>;
export const profileMutations = Object.fromEntries(profileSections.map(section => [section.key, gql`
  mutation SaveParticipantProfile${section.key}($odrnumber: Int!, $entity: String!, $key: String, $revision: String, $fields: [ParticipantProfileChange!]!) {
    saveParticipantProfile${section.key}(odrnumber: $odrnumber, entity: $entity, key: $key, revision: $revision, fields: $fields) { success id key written warnings }
  }
`])) as Record<ProfileSection['key'], DocumentNode>;
export const PROFILE_CATALOG = gql`
  query ParticipantProfileCatalog {
    participantProfileCatalog { entities { name label section } }
  }
`;
export const PROFILE_CHANGES = gql`
  query ParticipantProfileSyncChanges($odrnumber: Int!, $entity: String!, $offset: Int) {
    participantProfileSyncChanges(odrnumber: $odrnumber, entity: $entity, offset: $offset) {
      ...ProfileFields
      entities { canReplace records { sources { origin participant timestamp author revision removed values { name value } } } }
    }
  }
  ${PROFILE_FIELDS}
`;
export const SYNC_PROFILE = gql`
  mutation SyncParticipantProfileRecord($odrnumber: Int!, $entity: String!, $key: String!, $revision: String!, $sourceRevision: String, $supersededKey: String, $supersededRevision: String) {
    syncParticipantProfileRecord(odrnumber: $odrnumber, entity: $entity, key: $key, revision: $revision, sourceRevision: $sourceRevision, supersededKey: $supersededKey, supersededRevision: $supersededRevision) { success id key written }
  }
`;
export const MULTIPLE_ENROLLMENTS = gql`
  query ParticipantMultipleEnrollments($after: Int, $limit: Int) {
    participantMultipleEnrollments(after: $after, limit: $limit) {
      hasMore next
      entries { odrnumber primary recommendation needsReview revision enrollments { participant program disposition activity eligible } }
    }
  }
`;
export const PROFILE_REFERENCE = gql`
  query ParticipantProfileForProgramNumber($participant: Int!) {
    participantProfileForProgramNumber(participant: $participant) { odrnumber }
  }
`;
export const PROFILE_MEDCALL_REFERENCE = gql`
  query ParticipantProfileForMedcallNumber($medcall: String!) {
    participantProfileForMedcallNumber(medcall: $medcall) { odrnumber }
  }
`;
export const PROFILE_MEDCALL_TIMELINE = gql`
  query ParticipantProfileMedcallTimeline($odrnumber: Int!, $from: String!, $to: String!, $offset: Int) {
    participantProfileMedcall(odrnumber: $odrnumber, entity: "medcall_timelines", from: $from, to: $to, offset: $offset) {
      ...ProfileFields
      entities { records { references { name key values { name value } } } }
    }
  }
  ${PROFILE_FIELDS}
`;
export const PROFILE_INCIDENTS = gql`
  query ParticipantProfileIncidents($odrnumber: Int!, $before: String, $limit: Int) {
    participantProfileIncidents(odrnumber: $odrnumber, before: $before, limit: $limit) {
      hasIdentity hasMore next
      items { id incidentDate incidentTime program reportCategory organizationName incidentLocationName loginName primaryContactName incidentClientsNames incidentClientsCid }
    }
  }
`;
export const PROFILE_NOTIFICATIONS = gql`
  query ParticipantProfileNotifications($offset: Int) {
    participantProfileNotifications(offset: $offset) { hasMore next items { id emailid title program group subgroup } }
  }
`;
export const COMPOSE_PROFILE_NOTIFICATION = gql`
  query ComposeParticipantProfileNotification($odrnumber: Int!, $template: String!) {
    composeParticipantProfileNotification(odrnumber: $odrnumber, template: $template) { title toaddress ccaddress replytoaddress subject body }
  }
`;
export const SEND_PROFILE_NOTIFICATION = gql`
  mutation SendParticipantProfileNotification($odrnumber: Int!, $input: ParticipantProfileNotificationInput!) {
    sendParticipantProfileNotification(odrnumber: $odrnumber, input: $input) { success queued }
  }
`;
export const PROFILE_MEDCALL_MESSAGE = gql`
  query ParticipantProfileMedcallMessage($odrnumber: Int!, $omittedFields: [String!]) {
    participantProfileMedcallMessage(odrnumber: $odrnumber, omittedFields: $omittedFields) {
      revision subject body toaddress ccaddress providerRecipientFound
    }
  }
`;
export const SEND_PROFILE_MEDCALL_MESSAGE = gql`
  mutation SendParticipantProfileMedcallMessage($odrnumber: Int!, $revision: String!, $toaddress: String!, $ccaddress: String,
    $body: String!, $notes: String, $urgent: Boolean, $requestId: String!, $omittedFields: [String!]) {
    sendParticipantProfileMedcallMessage(odrnumber: $odrnumber, revision: $revision, toaddress: $toaddress, ccaddress: $ccaddress,
      body: $body, notes: $notes, urgent: $urgent, requestId: $requestId, omittedFields: $omittedFields) { success queued }
  }
`;
export const PROFILE_SEARCH = gql`
  query ParticipantProfileSearch($search: String!, $after: Int, $limit: Int) {
    participantProfileSearch(search: $search, after: $after, limit: $limit) {
      hasMore next
      entries { odrnumber links { participant active } values { name value } }
    }
  }
`;
export const PROFILE_PROGRAM_SEARCH = gql`
  query ParticipantProfileProgramSearch($firstName: String, $lastName: String, $bookingNumber: String, $after: Int, $limit: Int) {
    participantProfileSearch(search: "", firstName: $firstName, lastName: $lastName, bookingNumber: $bookingNumber, after: $after, limit: $limit) {
      hasMore next
      entries { odrnumber links { participant active } values { name value } }
    }
  }
`;
export const PROFILE_MEDCALL_SEARCH = gql`
  query ParticipantProfileMedcallSearch($search: String!, $after: Int, $limit: Int) {
    participantProfileSearch: participantProfileMedcallSearch(search: $search, after: $after, limit: $limit) {
      hasMore next
      entries { odrnumber links { participant active } values { name value } }
    }
  }
`;
export const PROFILE_MEDCALL_CLIENTS = gql`
  query ParticipantProfileMedcallClients($page: Int, $limit: Int, $scope: String, $status: String, $search: String,
    $firstname: String, $lastname: String, $nurse: String, $programs: [String!], $providers: [String!], $facilities: [Int], $sort: String, $direction: String) {
    participantProfileMedcallClients(page: $page, limit: $limit, scope: $scope, status: $status, search: $search,
      firstname: $firstname, lastname: $lastname, nurse: $nurse, programs: $programs, providers: $providers,
      facilities: $facilities, sort: $sort, direction: $direction) {
      totalCount page hasMore fields programs providers facilities { value label }
      items { odrnumber participant revision values { name value } }
    }
  }
`;
export const PROFILE_MEDCALL_REPORT = gql`
  query ParticipantProfileMedcallReport($odrnumber: Int!, $from: String, $to: String) {
    participantProfileMedcallReport(odrnumber: $odrnumber, from: $from, to: $to) {
      from to identity { name value }
      medications { key values { name value } timelines { key values { name value } } }
      statuses { key values { name value } }
    }
  }
`;
export const PROFILE_MEDCALL_POPULATION = gql`
  query ParticipantProfileMedcallPopulation($after: Int, $limit: Int, $scope: String) {
    participantProfileMedcallPopulation(after: $after, limit: $limit, scope: $scope) {
      next hasMore items {
        odrnumber participant timestamp staff facility identity { name value }
        medications { key timestamp values { name value } timelines { key timestamp values { name value } } }
        statuses { key timestamp values { name value } }
      }
    }
  }
`;
export const PROFILE_MEDCALL_CLIENT_ACTION = gql`
  query ParticipantProfileMedcallClientAction($odrnumber: Int!, $medication: Boolean!) {
    identity: participantProfileMedcall(odrnumber: $odrnumber, entity: "medcall") { ...ProfileFields }
    medications: participantProfileMedcall(odrnumber: $odrnumber, entity: "medcall_medications") @include(if: $medication) { ...ProfileFields }
  }
  ${PROFILE_FIELDS}
`;
export const SAVE_LEGAL_RECORDS = gql`
  mutation SaveParticipantProfileLegalRecords($odrnumber: Int!, $records: [ParticipantProfileRecordInput!]!) {
    saveParticipantProfileLegalRecords(odrnumber: $odrnumber, records: $records) {
      success warnings records { success id key written }
    }
  }
`;
export const RESOLVE_ENROLLMENT = gql`
  mutation ResolveParticipantMultipleEnrollment($odrnumber: Int!, $revision: String!, $participant: Int) {
    resolveParticipantMultipleEnrollment(odrnumber: $odrnumber, revision: $revision, participant: $participant) { success written participant }
  }
`;
export const ARCHIVE_LEGAL_RECORD = gql`
  mutation ArchiveParticipantProfileLegalRecord($odrnumber: Int!, $entity: String!, $key: String!, $revision: String!) {
    archiveParticipantProfileLegalRecord(odrnumber: $odrnumber, entity: $entity, key: $key, revision: $revision) { success written warnings }
  }
`;
export const UPLOAD_PROFILE_SCAN = gql`
  mutation UploadParticipantProfileScan($odrnumber: Int!, $type: String!, $label: String, $filename: String!, $content: String!) {
    uploadParticipantProfileScan(odrnumber: $odrnumber, type: $type, label: $label, filename: $filename, content: $content) { success warnings }
  }
`;
export const DOWNLOAD_PROFILE_SCAN = gql`
  query DownloadParticipantProfileScan($odrnumber: Int!, $key: String!) {
    downloadParticipantProfileScan(odrnumber: $odrnumber, key: $key) { filename content contentType }
  }
`;
export const UPLOAD_PROFILE_MINUTES = gql`
  mutation UploadParticipantProfileMinutes($odrnumber: Int!, $key: String!, $revision: String!, $filename: String!, $content: String!) {
    uploadParticipantProfileMinutes(odrnumber: $odrnumber, key: $key, revision: $revision, filename: $filename, content: $content) { success warnings }
  }
`;
export const DOWNLOAD_PROFILE_MINUTES = gql`
  query DownloadParticipantProfileMinutes($odrnumber: Int!, $key: String!) {
    downloadParticipantProfileMinutes(odrnumber: $odrnumber, key: $key) { filename content contentType }
  }
`;
export const CREATE_PROFILE_EVENT = gql`
  mutation CreateParticipantProfileEvent($odrnumber: Int!, $key: String!, $fields: [ParticipantProfileChange!]!) {
    saveParticipantProfileEvents: createParticipantProfileEvent(odrnumber: $odrnumber, key: $key, fields: $fields) { success warnings }
  }
`;

export const profileArchiveMutations: Partial<Record<ProfileSection['key'], DocumentNode>> = {
  Legal: ARCHIVE_LEGAL_RECORD,
  ...Object.fromEntries(['Identity', 'Medical', 'Care', 'Enrollment', 'Notes', 'Events', 'Documents', 'Medcall'].map(key => [key, gql`
    mutation ArchiveParticipantProfile${key}($odrnumber: Int!, $entity: String!, $key: String!, $revision: String!) {
      archiveParticipantProfile${key}(odrnumber: $odrnumber, entity: $entity, key: $key, revision: $revision) { success written warnings }
    }
  `])),
};
